import React, { useEffect, useState } from 'react'
import {
  CCloseButton,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCalendar,
  cilCheckCircle,
  cilCreditCard,
  cilLayers,
  cilPeople,
  cilShieldAlt,
  cilStar,
  cilUser,
  cilXCircle,
} from '@coreui/icons'

import ToastComponent from 'src/components/common/TaostComponent'
import UserService from 'src/service/UserService'
import {
  GameTypeBadge,
  PlanBadge,
  PLATFORM_FEE_RATE,
  collectionMethodLabel,
  formatMoney,
  gameModeLabel,
  gamePassLabel,
  sumAmounts,
} from './mctGameHelpers'

const TABS = [
  { key: 'structure', label: 'Structure', icon: cilLayers },
  { key: 'fixtures', label: 'Fixtures', icon: cilCalendar },
  { key: 'addons', label: 'PRO Add-Ons', icon: cilStar },
  { key: 'payments', label: 'Payments', icon: cilCreditCard },
]

const ADD_ONS = [
  { key: 'is_bonus_card_activate', label: 'Bonus Cards' },
  { key: 'is_totr_activate', label: 'Team of the Round' },
  { key: 'is_bracket_battle_activate', label: 'Bracket Battle' },
  { key: 'is_h2h_activate', label: 'Head-2-Head' },
  { key: 'is_voting_activate', label: 'Votes' },
]

const FIXTURE_STAGES = [
  { key: 'notStartedFixture', label: 'Not Yet Started', tone: 'secondary' },
  { key: 'inProgressFixture', label: 'In Progress', tone: 'warning' },
  { key: 'completedFixture', label: 'Completed', tone: 'success' },
]

const DetailList = ({ title, rows }) => (
  <div className="mg-section">
    {title && <div className="mg-section-title">{title}</div>}
    <dl className="mg-dl">
      {rows.map((row) => (
        <div className="mg-dl-row" key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value ?? '-'}</dd>
        </div>
      ))}
    </dl>
  </div>
)

const StructureTab = ({ game }) => (
  <>
    <DetailList
      title="Team rules"
      rows={[
        { label: 'Team Size', value: game.team_size ?? 0 },
        { label: 'Game Mode', value: gameModeLabel(game.team_view) },
        { label: 'Salary Cap', value: `$${game.salary_cap ?? 0}` },
        { label: 'Player Selection Limit', value: game.team_selection_limit ?? 0 },
      ]}
    />
    <DetailList
      title="Trades"
      rows={[
        { label: 'Trades Allowed', value: game.trades ?? 0 },
        { label: 'Trades Used', value: game.trade_used ?? 0 },
      ]}
    />
    <DetailList
      title="Visibility"
      rows={[
        { label: 'Game Privacy', value: game.game_privacy === 1 ? 'Public' : 'Private' },
        { label: 'Squad Alert', value: game.player_alert === 1 ? 'Yes' : 'No' },
      ]}
    />
  </>
)

const FixturesTab = ({ game }) => {
  const total = Number(game.totalFixture) || 0
  return (
    <div className="mg-section">
      <div className="d-flex justify-content-between align-items-baseline mb-2">
        <div className="mg-section-title mb-0">Fixture progress</div>
        <div className="um-muted small">{total} total</div>
      </div>
      {total === 0 ? (
        <div className="mg-empty">No fixtures have been created for this game yet.</div>
      ) : (
        <>
          <div className="mg-progress" aria-hidden="true">
            {FIXTURE_STAGES.map((stage) => {
              const count = Number(game[stage.key]) || 0
              if (!count) return null
              return (
                <div
                  key={stage.key}
                  className={`mg-progress-seg mg-tone-${stage.tone}`}
                  style={{ width: `${(count / total) * 100}%` }}
                />
              )
            })}
          </div>
          <div className="mg-fixture-grid">
            {FIXTURE_STAGES.map((stage) => (
              <div className="mg-fixture-tile" key={stage.key}>
                <span className={`mg-dot mg-tone-${stage.tone}`} />
                <div>
                  <div className="mg-fixture-value">{Number(game[stage.key]) || 0}</div>
                  <div className="um-muted small">{stage.label}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const AddOnsTab = ({ game }) => (
  <div className="mg-section">
    <div className="mg-section-title">PRO add-ons</div>
    <div className="mg-addon-list">
      {ADD_ONS.map((addon) => {
        const on = game[addon.key] === 1
        return (
          <div className={`mg-addon${on ? ' mg-addon--on' : ''}`} key={addon.key}>
            <CIcon icon={on ? cilCheckCircle : cilXCircle} />
            <span className="flex-grow-1">{addon.label}</span>
            <span className="mg-addon-state">{on ? 'Active' : 'Off'}</span>
          </div>
        )
      })}
    </div>
  </div>
)

const PaymentsTab = ({ game }) => {
  const symbol = game.entry_price_symbol
  const stripeGross = sumAmounts(game.stripPayment)
  const manual = sumAmounts(game.manualPayments)
  const stripeNet = stripeGross * (1 - PLATFORM_FEE_RATE)
  const platformFee = stripeGross * PLATFORM_FEE_RATE
  const totalEarning = stripeGross + manual

  return (
    <>
      <div className="mg-money-grid">
        <div className="mg-money-tile mg-money-tile--primary">
          <div className="um-muted small">Total Earning</div>
          <div className="mg-money-value">{formatMoney(symbol, totalEarning)}</div>
        </div>
        <div className="mg-money-tile">
          <div className="um-muted small">Stripe Collection</div>
          <div className="mg-money-value">{formatMoney(symbol, stripeNet)}</div>
          <div className="um-muted small">after 15% fee</div>
        </div>
        <div className="mg-money-tile">
          <div className="um-muted small">Manual Collection</div>
          <div className="mg-money-value">{formatMoney(symbol, manual)}</div>
        </div>
      </div>
      <DetailList
        title="Game pass"
        rows={[
          { label: 'Game Pass', value: gamePassLabel(game.collect_game_pass) },
          {
            label: 'Collection Method',
            value: collectionMethodLabel(game.collect_game_pass) || '-',
          },
          { label: 'Collection Required', value: game.collect_game_pass == 1 ? 'Yes' : 'No' },
          { label: 'Entry Fee', value: `${symbol ?? '$'}${game.entry_price ?? 0}` },
          { label: 'Platform Fee (15% of Stripe)', value: formatMoney(symbol, platformFee) },
        ]}
      />
    </>
  )
}

const MctGameDetailPanel = ({ game: row, visible, onClose }) => {
  const [activeTab, setActiveTab] = useState('structure')
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!visible || !row?.id) return
    let cancelled = false
    setActiveTab('structure')
    setDetail(null)
    setError(null)
    setLoading(true)
    UserService.getGameDetails(row.id)
      .then((res) => {
        if (!cancelled) setDetail(res.data)
      })
      .catch((e) => {
        if (cancelled) return
        const message = e?.response?.data?.message || 'Could not load game details'
        setError(message)
        ToastComponent(message, 'error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [visible, row?.id])

  const game = detail || {}
  // Member/team/player counts come from the All Games listing row, so they are only
  // shown when the panel was opened from that tab.
  const stats = [
    { label: 'Members', value: row?.member_count, icon: cilUser, tone: 'primary' },
    { label: 'Teams', value: row?.team_count, icon: cilShieldAlt, tone: 'info' },
    { label: 'Players', value: row?.player_count, icon: cilPeople, tone: 'success' },
    {
      label: 'Fixtures',
      value: detail ? detail.totalFixture ?? 0 : undefined,
      icon: cilCalendar,
      tone: 'warning',
    },
  ].filter((s) => s.value !== undefined && s.value !== null)

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={onClose}
      className="mg-panel"
      style={{ width: 600 }}
    >
      <COffcanvasHeader className="align-items-start">
        <div className="min-w-0">
          <div className="fs-5 fw-semibold text-truncate">
            {detail?.game_name || row?.game_name}
          </div>
          <div className="um-muted small">
            Game ID {row?.id}
            {row?.hub_name ? ` · ${row.hub_name}` : ''}
            {row?.hub_id ? ` (Hub ID ${row.hub_id})` : ''}
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <GameTypeBadge type={detail?.game_type ?? row?.game_type} />
            {row?.pricing_plan !== undefined && <PlanBadge plan={row.pricing_plan} />}
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>

      <COffcanvasBody>
        {stats.length > 0 && (
          <div className="mg-stat-grid">
            {stats.map((s) => (
              <div className="mg-stat" key={s.label}>
                <div className={`ge-stat-icon ge-stat-icon--${s.tone}`}>
                  <CIcon icon={s.icon} />
                </div>
                <div>
                  <div className="mg-stat-value">{s.value}</div>
                  <div className="um-muted small">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <ul className="nav ge-segmented-tabs mb-3" role="tablist">
          {TABS.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`nav-link${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <CIcon icon={tab.icon} className="me-1" />
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {loading && (
          <div className="d-flex align-items-center gap-2 um-muted py-4 justify-content-center">
            <CSpinner size="sm" /> Loading game details...
          </div>
        )}
        {!loading && error && <div className="mg-empty">{error}</div>}
        {!loading && detail && (
          <>
            {activeTab === 'structure' && <StructureTab game={game} />}
            {activeTab === 'fixtures' && <FixturesTab game={game} />}
            {activeTab === 'addons' && <AddOnsTab game={game} />}
            {activeTab === 'payments' && <PaymentsTab game={game} />}
          </>
        )}
      </COffcanvasBody>
    </COffcanvas>
  )
}

export default MctGameDetailPanel
